/*
 * Copyright 2021 Palantir Technologies, Inc. All rights reserved.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

// Disable Lint for 3rd party code
/* eslint-disable react/prop-types, react-hooks/exhaustive-deps, react-hooks/rules-of-hooks */

import { Classes } from '@blueprintjs/core';
import classNames from 'classnames';
import React, { useCallback, useState } from 'react';

import SimplePanelView from './simplePanelView';

/**
 * Customized Blueprint.js PanelStack2 v3.44.2
 *
 * Changes made:
 *
 * - Removed problematic CSS Animations.
 *   If user clicks panel in leaving animation (eg. clicks fast twice) the link is activated again,
 *   inserting another copy of panel into stack.
 *   ALso in controlled mode, the animation is always in the push direction (right to left).
 *   Looks like there is no other way how to implement back button behavior with pop direction.
 * - Removed fixed height requirement, panel stack now fits to size of current panel.
 *   This was another downside required by CSS Animations.
 *   https://github.com/palantir/blueprint/issues/2777
 * - Removed the now useless direction state.
 * @param {object} props
 * @returns {object}
 */
const SimplePanelStack = props => {
  const { renderActivePanelOnly = true, showPanelHeader = true } = props;

  const [localStack, setLocalStack] = useState(
    props.initialPanel !== undefined ? [props.initialPanel] : [],
  );
  const stack = props.stack != null ? props.stack.slice().reverse() : localStack;

  if (stack.length === 0) {
    return null;
  }

  const handlePanelOpen = useCallback(
    panel => {
      props.onOpen?.(panel);
      if (props.stack == null) {
        setLocalStack(prevStack => [panel, ...prevStack]);
      }
    },
    [props.onOpen],
  );
  const handlePanelClose = useCallback(
    panel => {
      // only remove this panel if it is at the top and not the only one.
      if (stack[0] !== panel || stack.length <= 1) {
        return;
      }
      props.onClose?.(panel);
      if (props.stack == null) {
        setLocalStack(prevStack => prevStack.slice(1));
      }
    },
    [stack, props.onClose],
  );

  const panelsToRender = renderActivePanelOnly ? [stack[0]] : stack;
  const panels = panelsToRender
    .map((panel, index) => {
      // With renderActivePanelOnly={false} we would keep all the CSSTransitions rendered,
      // therefore they would not trigger the "enter" transition event as they were entered.
      // To force the enter event, we want to change the key, but stack.length is not enough
      // and a single panel should not rerender as long as it's hidden.
      // This key contains two parts: first one, stack.length - index is constant (and unique) for each panel,
      // second one, active changes only when the panel becomes or stops being active.
      const layer = stack.length - index;
      const key = renderActivePanelOnly ? stack.length : layer;

      return (
        <div className={Classes.PANEL_STACK2} key={key}>
          <SimplePanelView
            onClose={handlePanelClose}
            onOpen={handlePanelOpen}
            panel={panel}
            previousPanel={stack[index + 1]}
            showHeader={showPanelHeader}
          />
        </div>
      );
    })
    .reverse();

  const classes = classNames(Classes.PANEL_STACK2, props.className);

  return <div className={classes}>{panels}</div>;
};

export default SimplePanelStack;
